import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';
const NAVY = '#0d1b3e';
const GOLD = '#c9a227';

// Dashboard onboarding checklist. Eight actions split across two groups -
// setup (one-time config) and try-each-section (find the value). Most flags
// come from the server (/api/onboarding/checklist) which derives them from
// existing data. The "View your P&L" item is purely a UI action so the
// client tracks that in localStorage and the Money page writes the flag on
// mount.
//
// Behavior:
// - Auto-detects completion. No manual check / uncheck.
// - Not dismissible, disappears entirely only when all items are done.
// - Hidden until the data loads so it doesn't flash "0/8" on every render.
// - Per-user localStorage key for the P&L visit flag.

export const PNL_FLAG_KEY = 'scn-checklist-pnl-viewed';

// Shared step list, used by both the (legacy) Dashboard card and the floating
// SetupWidget so they never drift. Each step can later carry an optional
// `videoUrl`; when present the UI shows a "Watch Video" link (videos land in §8).
export const ITEMS = [
  {
    id: 'ebayConnected',
    group: 'setup',
    title: 'Connect eBay',
    desc: 'Imports your active listings and starts syncing orders every 2 minutes.',
    // from=checklist → after the eBay OAuth round-trip, Settings sends them back
    // to the dashboard checklist instead of stranding them in Settings.
    href: '/settings?tab=integrations&from=checklist',
    videoUrl: '/videos/connect-ebay.mp4', // §8 clip 2, 28s walkthrough (hosted natively like the welcome clip)
  },
  {
    id: 'shipFromSet',
    group: 'setup',
    title: 'Set your ship-from address',
    desc: "Required before you can buy labels or list to eBay.",
    href: '/settings?tab=shipping',
  },
  {
    id: 'firstCard',
    group: 'try',
    title: 'Scan your first card',
    desc: 'See what the AI does in 10 seconds. Front-of-card photo is enough.',
    href: '/inventory',
    videoUrl: '/videos/ai-scan.mp4', // §8 clip 3, 20s AI Scan walkthrough
  },
  {
    id: 'costEntered',
    group: 'try',
    title: 'Add your cost on a card',
    desc: "Enter what you paid. It powers profit-per-card and your real P&L. Got a lot already? Inventory → Add Costs imports a spreadsheet. Bought on eBay? Settings → eBay → Import Purchases auto-fills it.",
    href: '/inventory',
  },
  {
    id: 'ordersImported',
    group: 'try',
    title: 'See your eBay orders',
    desc: 'Once eBay is connected, your orders + real fees show up here automatically.',
    href: '/orders',
  },
  {
    id: 'pnlViewed',
    group: 'try',
    title: 'View your real P&L',
    desc: 'Revenue, actual eBay fees, and profit per card (not estimates).',
    href: '/accounting',
    videoUrl: '/videos/pnl.mp4', // §8 clip 16, 45s P&L + how costs get in
  },
];

export default function OnboardingChecklist() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await axios.get(`${API}/onboarding/checklist`);
        if (cancelled) return;
        setStatus({
          ...res.data,
          pnlViewed: localStorage.getItem(PNL_FLAG_KEY) === '1',
        });
      } catch {
        if (!cancelled) setStatus({}); // fail open, render empty checklist rather than crash
      }
    }
    load();
    // Re-load when the tab regains focus so a quick visit to Settings (e.g.
    // connect eBay) is reflected without a manual refresh.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, []);

  if (status === null) return null; // loading
  const items = ITEMS.map(i => ({ ...i, done: !!status[i.id] }));
  const doneCount = items.filter(i => i.done).length;
  // All done, hide the card entirely. We never come back, by design.
  if (doneCount === items.length) return null;

  const pct = Math.round((doneCount / items.length) * 100);

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      padding: '20px 22px',
      marginBottom: 20,
      boxShadow: '0 1px 3px rgba(13,27,62,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: GOLD, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Get the most out of SCN</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: NAVY, letterSpacing: '-0.3px' }}>{doneCount} of {items.length} done</div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#666', textAlign: 'right', whiteSpace: 'nowrap', paddingTop: 4 }}>{pct}%</div>
      </div>
      {/* Progress bar */}
      <div style={{ background: '#f1f3f9', borderRadius: 999, height: 6, overflow: 'hidden', marginBottom: 18 }}>
        <div style={{
          background: `linear-gradient(90deg, ${GOLD}, #e0b94b)`,
          width: `${pct}%`, height: '100%', borderRadius: 999, transition: 'width 0.4s ease',
        }} />
      </div>

      <Section label="Get set up" items={items.filter(i => i.group === 'setup')} />
      <div style={{ height: 14 }} />
      <Section label="Try each section" items={items.filter(i => i.group === 'try')} />
    </div>
  );
}

function Section({ label, items }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, color: '#9aa1ad', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map(item => <Item key={item.id} item={item} />)}
      </div>
    </div>
  );
}

function Item({ item }) {
  return (
    <Link
      to={item.href}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '10px 12px', borderRadius: 9,
        background: item.done ? '#f6faf6' : 'transparent',
        border: `1px solid ${item.done ? '#cce7d0' : '#eef0f4'}`,
        textDecoration: 'none', color: 'inherit',
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => { if (!item.done) { e.currentTarget.style.background = '#fafbff'; e.currentTarget.style.borderColor = '#d8dceb'; } }}
      onMouseLeave={e => { if (!item.done) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#eef0f4'; } }}
    >
      <CheckCircle done={item.done} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: item.done ? '#16a34a' : NAVY, textDecoration: item.done ? 'line-through' : 'none' }}>{item.title}</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>{item.desc}</div>
      </div>
      {!item.done && <ArrowRight />}
    </Link>
  );
}

function CheckCircle({ done }) {
  if (done) {
    return (
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
    );
  }
  return (
    <div style={{
      width: 20, height: 20, borderRadius: '50%',
      border: '2px solid #cbd5e1', flexShrink: 0, marginTop: 1,
    }} />
  );
}

function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9aa1ad" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 4 }}>
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}

// Exported helper so other components (Money page) can set the P&L-viewed
// flag without importing the localStorage key string.
export function markPnlViewed() {
  try { localStorage.setItem(PNL_FLAG_KEY, '1'); } catch {}
}
